/* ------------------------------------------------------------------
   La.Voo.Doo — Heavy Heart Enchantment add-to-cart offer
   Gravitas Digital, 2026-09-12

   Behaviour, agreed with the client:
   - Fires every time a doll is added to the cart. Three dolls, three prompts.
   - Offers one Enchantment per doll added.
   - Never fires for the Enchantment itself or the test product.
   - Stays hidden when there is not enough Enchantment stock left to honour
     the offer, counting what is already sitting in the cart.
   - Still fires when a shopper asks for more of a doll than exists, which is
     easy to do because stock is 1 and the quantity box allows more. See the
     fallback further down; Gain emits no event at all in that case.

   It hooks Gain's own event bus rather than patching the theme:
     window.themeCore.EventBus.listen("cart:updated", ...)
     window.themeCore.CartApi.makeRequest(actions.ADD_TO_CART, { id, quantity })
     window.themeCore.CartNotification.open() / .close()
------------------------------------------------------------------ */

(function () {
	"use strict";

	var SELECTOR = ".js-lvd-ench";

	// How long after handling an add we treat a second trigger as a duplicate.
	var HANDLED_WINDOW = 2500;
	// How long to wait for an add to settle before the fallback checks the cart.
	var FALLBACK_DELAY = 1400;

	var LvdEnchantmentOffer = function () {
		var config = null;
		var root = null;
		var els = {};
		var pendingQty = 0;
		var lastFocused = null;
		var busy = false;
		var lastHandled = 0;

		function init() {
			root = document.querySelector(SELECTOR);

			if (!root) {
				return;
			}

			config = readConfig();

			if (!config || !config.variantId) {
				return;
			}

			els = {
				modal: root.querySelector(".js-lvd-ench-modal"),
				accept: root.querySelector(".js-lvd-ench-accept"),
				acceptLabel: root.querySelector(".js-lvd-ench-accept-label"),
				decline: root.querySelector(".js-lvd-ench-decline"),
				close: root.querySelector(".js-lvd-ench-close"),
				scrim: root.querySelector(".js-lvd-ench-scrim"),
				error: root.querySelector(".js-lvd-ench-error")
			};

			els.accept.addEventListener("click", onAccept);
			els.decline.addEventListener("click", onDecline);
			els.close.addEventListener("click", onDecline);
			els.scrim.addEventListener("click", onDecline);
			document.addEventListener("keydown", onKeydown);
			document.addEventListener("submit", onAddToCartSubmit, true);

			window.themeCore.EventBus.listen("cart:updated", onCartUpdated);
		}

		function readConfig() {
			var el = root.querySelector(".js-lvd-ench-config");

			try {
				return JSON.parse(el.textContent);
			} catch (e) {
				return null;
			}
		}

		/* How many dolls did this particular add put in the cart?
		   Mirrors Gain's own approach: the quantity on the returned line item is
		   the whole line, so prefer the quantity actually requested. */
		function countDollsAdded(data) {
			var actions = window.themeCore.CartApi.actions;

			if (data.action !== actions.ADD_TO_CART && data.action !== actions.ADD_TO_CART_MANY) {
				return 0;
			}

			if (!Array.isArray(data.items)) {
				return 0;
			}

			var requested = (data.params || [])
				.reduce(function (all, param) {
					return all.concat(Array.isArray(param) ? param : [param]);
				}, [])
				.filter(function (param) {
					return param && param.id !== undefined && param.quantity !== undefined;
				});

			return data.items.reduce(function (total, item) {
				if (config.excludeProductIds.indexOf(item.product_id) !== -1) {
					return total;
				}

				var match = requested.filter(function (param) {
					return String(param.id) === String(item.id);
				})[0];

				var qty = match ? Number(match.quantity) : Number(item.quantity);

				return total + (qty > 0 ? qty : 1);
			}, 0);
		}

		/* Enchantments already in the cart eat into what we can still offer. */
		function alreadyInCart() {
			return fetch(window.Shopify.routes.root + "cart.js", {
				headers: { Accept: "application/json" }
			})
				.then(function (res) {
					return res.json();
				})
				.then(function (cart) {
					return (cart.items || []).reduce(function (total, item) {
						return String(item.id) === String(config.variantId) ? total + item.quantity : total;
					}, 0);
				})
				.catch(function () {
					// If the cart cannot be read, fall back to offering nothing extra
					// rather than risking an oversell.
					return config.inventory;
				});
		}

		function onCartUpdated(data) {
			if (!data || !data.action) {
				return;
			}

			handleDolls(countDollsAdded(data));
		}

		/* The single decision point, shared by the event path and the fallback. */
		function handleDolls(dolls) {
			if (dolls < 1 || isOpen() || busy || !config.available) {
				return;
			}

			lastHandled = Date.now();

			alreadyInCart().then(function (held) {
				var remaining = config.inventory - held;

				if (remaining < 1) {
					return;
				}

				open(Math.min(dolls, remaining));
			});
		}

		/* ----------------------------------------------------------------
		   Fallback for partially filled adds.

		   Every doll has a stock of 1, and the product page still offers a
		   quantity box. Ask for 2 and Shopify adds 1, reporting "Only 1 item was
		   added to your cart due to availability". Gain treats that as an error
		   and never emits cart:updated, even though the doll really did go in.

		   So watch the add-to-cart form itself and diff the cart afterwards.
		   Whichever path gets there first wins; lastHandled stops both running.
		---------------------------------------------------------------- */
		function snapshotCart() {
			return fetch(window.Shopify.routes.root + "cart.js", {
				headers: { Accept: "application/json" }
			})
				.then(function (res) {
					return res.json();
				})
				.then(function (cart) {
					var map = {};

					(cart.items || []).forEach(function (item) {
						map[item.id] = { quantity: item.quantity, productId: item.product_id };
					});

					return map;
				})
				.catch(function () {
					return null;
				});
		}

		function dollsAddedBetween(before, after) {
			if (!before || !after) {
				return 0;
			}

			return Object.keys(after).reduce(function (total, variantId) {
				var line = after[variantId];

				if (config.excludeProductIds.indexOf(line.productId) !== -1) {
					return total;
				}

				var was = before[variantId] ? before[variantId].quantity : 0;
				var gained = line.quantity - was;

				return total + (gained > 0 ? gained : 0);
			}, 0);
		}

		function onAddToCartSubmit(event) {
			var target = event.target;
			var form = target && target.closest && target.closest('form[action*="/cart/add"]');

			if (!form || isOpen() || busy) {
				return;
			}

			snapshotCart().then(function (before) {
				setTimeout(function () {
					if (Date.now() - lastHandled < HANDLED_WINDOW || isOpen() || busy) {
						return;
					}

					snapshotCart().then(function (after) {
						handleDolls(dollsAddedBetween(before, after));
					});
				}, FALLBACK_DELAY);
			});
		}

		function open(quantity) {
			pendingQty = quantity;
			lastFocused = document.activeElement;

			els.error.textContent = "";
			els.accept.disabled = false;
			els.acceptLabel.textContent = quantity > 1 ? config.acceptLabel + " × " + quantity : config.acceptLabel;

			// Gain shows its own "added to cart" popup on this same event.
			// Two overlays at once is a mess, so stand ours in its place.
			closeThemeNotification();

			root.classList.add("is-open");
			document.body.style.overflow = "hidden";

			els.modal.focus();

			if (window.themeCore.utils && window.themeCore.utils.trapFocus) {
				window.themeCore.utils.trapFocus(els.modal);
			}
		}

		function close() {
			root.classList.remove("is-open");
			document.body.style.overflow = "";
			pendingQty = 0;

			if (window.themeCore.utils && window.themeCore.utils.removeTrapFocus) {
				window.themeCore.utils.removeTrapFocus();
			}

			if (lastFocused && lastFocused.focus) {
				lastFocused.focus();
			}
		}

		function isOpen() {
			return root.classList.contains("is-open");
		}

		function closeThemeNotification() {
			var notification = window.themeCore.CartNotification;

			if (notification && notification.close) {
				notification.close();
			}
		}

		/* Declining should still reassure them the doll made it into the cart,
		   so hand the moment back to Gain's own notification. */
		function showThemeNotification() {
			var notification = window.themeCore.CartNotification;

			if (notification && notification.open) {
				notification.open();
			}
		}

		function onDecline(event) {
			if (event) {
				event.preventDefault();
			}

			close();
			showThemeNotification();
		}

		function onKeydown(event) {
			if (event.key === "Escape" && isOpen()) {
				onDecline(event);
			}
		}

		function onAccept(event) {
			event.preventDefault();

			if (busy || pendingQty < 1) {
				return;
			}

			busy = true;
			els.accept.disabled = true;
			els.acceptLabel.textContent = config.addingLabel;

			window.themeCore.CartApi.makeRequest(window.themeCore.CartApi.actions.ADD_TO_CART, {
				id: config.variantId,
				quantity: pendingQty
			})
				.then(function () {
					busy = false;
					close();
				})
				.catch(function (error) {
					busy = false;
					els.accept.disabled = false;
					els.acceptLabel.textContent = config.acceptLabel;
					els.error.textContent = (error && error.description) || config.errorLabel;
				});
		}

		return Object.freeze({
			init: init,
			open: open,
			close: close,
			isOpen: isOpen
		});
	};

	var action = function () {
		window.themeCore.LvdEnchantmentOffer = window.themeCore.LvdEnchantmentOffer || LvdEnchantmentOffer();
		window.themeCore.LvdEnchantmentOffer.init();
	};

	if (window.themeCore && window.themeCore.loaded) {
		action();
	} else {
		document.addEventListener("theme:all:loaded", action, { once: true });
	}
})();
